import React, { forwardRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Checkbox,
  ListItemText,
  FormControl,
  SelectChangeEvent,
  CircularProgress,
  InputBase,
  MenuItem,
} from "@mui/material";
import { BottomArrowIcon, SearchIcon, CrossIcon, CheckIcon } from "../Icons";
import {
  IconContainer,
  Label,
  StyledSelect,
  ValueContainer,
  ChipsContainer,
  StyledChip,
  SearchContainer,
  StyledMenuItem,
  SecondaryLabel,
  menuPaperStyles,
} from "./Select.styled";
import { colors } from "../../theme/colors";

export interface SelectOption {
  value: string | number;
  label: string;
  chipLabel?: string;
  secondaryLabel?: string;
}

interface SelectProps {
  label?: string;
  placeholder?: string;
  value?: string | number | (string | number)[];
  onChange?: (value: string | number | (string | number)[]) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  name?: string;
  id?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  multiple?: boolean;
  options: SelectOption[];
  emptyText?: string;
  loading?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearchChange?: (query: string) => void;
  showChips?: boolean;
}

const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      label,
      placeholder,
      value,
      onChange,
      onFocus,
      onBlur,
      onOpen: onOpenProp,
      onClose: onCloseProp,
      disabled = false,
      fullWidth = false,
      width,
      name,
      id,
      error = false,
      helperText,
      required = false,
      multiple = false,
      options = [],
      emptyText,
      loading = false,
      loadingMore = false,
      onLoadMore,
      searchable = false,
      searchPlaceholder,
      onSearchChange,
      showChips = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const handleMenuScroll = (e: React.UIEvent<HTMLElement>) => {
      if (!onLoadMore || loading || loadingMore) return;
      const el = e.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
      if (nearBottom) onLoadMore();
    };
    const defaultEmptyText = emptyText || t("select.noOptions");

    const handleChange = (event: SelectChangeEvent<unknown>) => {
      const selectedValue = event.target.value as
        | string
        | number
        | (string | number)[];
      onChange?.(selectedValue);
    };

    const handleOpen = () => {
      setOpen(true);
      onOpenProp?.();
    };

    const handleClose = () => {
      setOpen(false);
      setSearchQuery("");
      onSearchChange?.("");
      onCloseProp?.();
    };

    const handleRemoveChip = (
      valueToRemove: string | number,
      event: React.MouseEvent,
    ) => {
      event.stopPropagation();
      if (multiple && Array.isArray(value)) {
        const newValue = value.filter((v) => v !== valueToRemove);
        onChange?.(newValue);
      }
    };

    const filteredOptions = useMemo(() => {
      if (!searchable || onSearchChange) return options;
      if (!searchQuery.trim()) return options;
      const query = searchQuery.toLowerCase();
      return options.filter((opt) => opt.label.toLowerCase().includes(query));
    }, [options, searchQuery, searchable, onSearchChange]);

    const getDisplayValue = () => {
      if (value === undefined || value === null) return placeholder || "";

      if (multiple && Array.isArray(value)) {
        if (value.length === 0) return placeholder || "";
        if (showChips) return "";
        const selectedLabels = value
          .map((val) => {
            const option = options.find((opt) => opt.value === val);
            return option?.label || "";
          })
          .filter(Boolean);
        return selectedLabels.join(", ");
      }

      const option = options.find((opt) => opt.value === value);
      return option?.label || placeholder || "";
    };

    const selectedOptions = useMemo(() => {
      if (!multiple || !Array.isArray(value)) return [];
      return value
        .map((v) => options.find((opt) => opt.value === v))
        .filter(Boolean) as SelectOption[];
    }, [value, options, multiple]);

    return (
      <Box sx={{ width: fullWidth ? "100%" : width }}>
        {label && (
          <Label>
            {label}
            {required && (
              <Box component="span" sx={{ color: "inherit" }}>
                {" "}
                *
              </Box>
            )}
          </Label>
        )}
        <FormControl fullWidth sx={{ height: "100%", width: "100%" }}>
          <StyledSelect
            ref={ref}
            value={value || (multiple ? [] : "")}
            onChange={handleChange}
            disabled={disabled}
            multiple={multiple}
            displayEmpty
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            error={error}
            IconComponent={() => (
              <IconContainer>
                <BottomArrowIcon width="16px" height="16px" />
              </IconContainer>
            )}
            renderValue={() => (
              <ValueContainer>
                {searchable && (
                  <Box sx={{ mr: 1, flexShrink: 0, display: "flex" }}>
                    <SearchIcon width="16px" height="16px" />
                  </Box>
                )}
                {showChips && selectedOptions.length > 0 ? (
                  <ChipsContainer>
                    {selectedOptions.map((opt) => (
                      <StyledChip
                        key={opt.value}
                        label={opt.chipLabel || opt.label}
                        size="small"
                        onDelete={(e) => handleRemoveChip(opt.value, e)}
                        deleteIcon={
                          <Box
                            component="span"
                            sx={{ cursor: "pointer", display: "flex" }}
                          >
                            <CrossIcon width="12" height="12" />
                          </Box>
                        }
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    ))}
                  </ChipsContainer>
                ) : (
                  <Typography
                    sx={{
                      color:
                        value !== undefined &&
                        value !== null &&
                        (Array.isArray(value) ? value.length > 0 : true)
                          ? `${colors.text.primary}!important`
                          : `${colors.text.secondary}!important`,
                      flex: 1,
                    }}
                  >
                    {getDisplayValue()}
                  </Typography>
                )}
              </ValueContainer>
            )}
            name={name}
            id={id}
            onFocus={onFocus}
            onBlur={onBlur}
            MenuProps={{
              autoFocus: false,
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
              PaperProps: {
                sx: menuPaperStyles,
                onScroll: handleMenuScroll,
              },
            }}
          >
            {searchable && (
              <SearchContainer>
                <InputBase
                  placeholder={
                    searchPlaceholder || t("select.search", "Search")
                  }
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    onSearchChange?.(e.target.value);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoFocus
                  fullWidth
                  sx={{
                    fontSize: "14px",
                    "& input": {
                      padding: "4px 0",
                    },
                  }}
                  startAdornment={
                    <Box sx={{ mr: 1, display: "flex" }}>
                      <SearchIcon width="16px" height="16px" />
                    </Box>
                  }
                />
              </SearchContainer>
            )}
            {loading ? (
              <MenuItem disabled>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    py: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              </MenuItem>
            ) : filteredOptions.length === 0 ? (
              <MenuItem disabled>
                <ListItemText
                  primary={
                    searchQuery
                      ? t("select.noResults", "No results found")
                      : defaultEmptyText
                  }
                />
              </MenuItem>
            ) : (
              filteredOptions.map((option) => {
                const isSelected =
                  multiple &&
                  Array.isArray(value) &&
                  value.includes(option.value);
                return (
                  <StyledMenuItem
                    key={option.value}
                    value={option.value}
                    isSelected={isSelected}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", flex: 1 }}
                    >
                      {multiple && !showChips && (
                        <Checkbox checked={isSelected} sx={{ mr: 1, p: 0 }} />
                      )}
                      <ListItemText primary={option.label} />
                    </Box>
                    {option.secondaryLabel && (
                      <SecondaryLabel>{option.secondaryLabel}</SecondaryLabel>
                    )}
                    {showChips && (
                      <Box
                        sx={{
                          ml: 2,
                          display: "flex",
                          width: "16px",
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && (
                          <CheckIcon
                            fill={colors.status.activeText}
                            width="16"
                            height="16"
                          />
                        )}
                      </Box>
                    )}
                  </StyledMenuItem>
                );
              })
            )}
            {loadingMore && (
              <MenuItem disabled>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    py: 1,
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              </MenuItem>
            )}
          </StyledSelect>
        </FormControl>

        {helperText && (
          <Typography
            variant="caption"
            sx={{
              color: error ? "status.error" : "text.secondary",
              fontSize: "12px",
              mt: 0.5,
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  },
);

Select.displayName = "Select";

export default Select;
